import { useMemo, type CSSProperties } from 'react'

import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CrossFilterChips } from '@/features/report/CrossFilterChips'
import { pairedKpi } from '@/features/report/dashboard-layout'
import { ReportFilterBar } from '@/features/report/ReportFilterBar'
import { LiveWidgetView } from '@/features/report/widgets/LiveWidget'
import { InTabs } from '@/features/report/widgets/shared'
import { useReportFilters, type ReportFilters } from '@/features/report/use-report-filters'
import {
  shortName,
  type DashboardReport,
  type ReportField,
  type StackItem,
} from '@/lib/cube-report'
import { cn } from '@/lib/utils'

/** Yığının parçaları tek kartta sekme olarak; sekme adı parçanın başlığı. */
function TabbedStack({ item, filters }: { item: StackItem; filters: ReportFilters }) {
  return (
    <Card className="gap-0 py-0">
      <Tabs defaultValue={item.widgets[0].key} className="gap-0">
        <CardHeader className="border-border border-b px-4 py-2">
          <TabsList>
            {item.widgets.map((widget, index) => (
              <TabsTrigger key={widget.key} value={widget.key}>
                {widget.title ?? String(index + 1)}
              </TabsTrigger>
            ))}
          </TabsList>
        </CardHeader>
        <CardContent className="p-0">
          <InTabs.Provider value>
            {item.widgets.map((widget) => (
              <TabsContent key={widget.key} value={widget.key}>
                <LiveWidgetView widget={widget} filters={filters} />
              </TabsContent>
            ))}
          </InTabs.Provider>
        </CardContent>
      </Tabs>
    </Card>
  )
}

/**
 * Pano: sayfa filtreleri + 12 kolonluk ızgarada parçalar. Hangi parçanın nerede,
 * hangi view'dan ne gösterdiği view meta'sından gelir (bkz. `parseWidgets`).
 */
export function DashboardReportView({ report }: { report: DashboardReport }) {
  const filters = useReportFilters(report)
  // Chip etiketi için kısa ad -> alan; aynı kısa ad her view'da aynı alandır.
  const fieldsByShortName = useMemo(() => {
    const map = new Map<string, ReportField>()
    for (const item of report.widgets) {
      for (const widget of item.type === 'stack' ? item.widgets : [item]) {
        for (const [name, member] of widget.fields) {
          if (!map.has(shortName(name))) map.set(shortName(name), member)
        }
      }
    }
    return map
  }, [report])
  return (
    <>
      <ReportFilterBar report={report} filters={filters} />
      <CrossFilterChips
        selections={filters.state.selections}
        resolveField={(member) => fieldsByShortName.get(member)}
        onChange={filters.setSelections}
      />
      {report.note && <p className="text-muted-foreground text-xs">{report.note}</p>}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-12 lg:items-start">
        {report.widgets.map((item, index) => (
          <div
            key={item.key}
            className={cn(
              'min-w-0 lg:col-span-(--span)',
              pairedKpi(report.widgets, index) ? 'col-span-1' : 'col-span-2',
              item.type === 'stack' && !item.tabs && 'flex flex-col gap-4',
              item.type === 'kpi' && 'lg:self-stretch',
            )}
            style={{ '--span': item.span } as CSSProperties}
          >
            {item.type === 'stack' && item.tabs ? (
              <TabbedStack item={item} filters={filters} />
            ) : item.type === 'stack' ? (
              item.widgets.map((widget) => (
                <LiveWidgetView key={widget.key} widget={widget} filters={filters} />
              ))
            ) : (
              <LiveWidgetView widget={item} filters={filters} />
            )}
          </div>
        ))}
      </div>
    </>
  )
}
